/**
 * ConnectTool — Connection creation UX
 * Per PLAN.md interactions/ConnectTool.js and PRD §4.4
 */

import { eventBus, Events } from '../core/EventBus.js';
import { Connection, ConnectionType } from '../models/Connection.js';
import { closestPointOnRectEdge } from '../utils/geometry.js';

export class ConnectTool {
  /**
   * @param {import('../core/Canvas.js').Canvas} canvas 
   * @param {import('../models/CanvasDocument.js').CanvasDocument} document 
   */
  constructor(canvas, document) {
    this.canvas = canvas;
    this.document = document;
    this.isActive = false;
    
    // Connection drawing state
    this.sourceNode = null;
    this.isDrawing = false;
    
    // Modifier keys for connection type
    this.connectionType = ConnectionType.ARROW;
  }

  /**
   * Start connection from a node
   * @param {string} nodeId 
   * @param {number} screenX 
   * @param {number} screenY 
   */
  startConnection(nodeId, screenX, screenY) {
    const node = this.document.getNode(nodeId);
    if (!node) return;
    
    this.sourceNode = node;
    this.isDrawing = true;
    this.canvas.isDrawingConnection = true;
    
    // Get edge point
    const center = node.getCenter();
    const screen = this.canvas.viewport.worldToScreen(center.x, center.y);
    
    this.canvas.connectionPreview = {
      startX: screen.x,
      startY: screen.y,
      endX: screenX,
      endY: screenY
    };
    
    eventBus.emit(Events.RENDER_REQUEST);
  }

  /**
   * Update connection preview
   * @param {number} screenX 
   * @param {number} screenY 
   * @param {boolean} altKey - Hold for line instead of arrow
   * @param {boolean} shiftKey - Hold for double arrow
   * @param {boolean} metaKey - Hold for dashed
   */
  updateConnection(screenX, screenY, altKey = false, shiftKey = false, metaKey = false) {
    if (!this.isDrawing) return;
    
    // Update connection type based on modifiers
    if (metaKey) {
      this.connectionType = ConnectionType.DASHED;
    } else if (shiftKey) {
      this.connectionType = ConnectionType.DOUBLE_ARROW;
    } else if (altKey) {
      this.connectionType = ConnectionType.LINE;
    } else {
      this.connectionType = ConnectionType.ARROW;
    }
    
    // Update endpoint
    this.canvas.connectionPreview.endX = screenX;
    this.canvas.connectionPreview.endY = screenY;
    
    eventBus.emit(Events.RENDER_REQUEST);
  }

  /**
   * Complete connection to a target node
   * @param {number} screenX 
   * @param {number} screenY 
   * @returns {Connection|null}
   */
  endConnection(screenX, screenY) {
    if (!this.isDrawing || !this.sourceNode) {
      this.cancel();
      return null;
    }
    
    // Find target node at position
    const worldPos = this.canvas.viewport.screenToWorld(screenX, screenY);
    const targetNode = this.document.getNodeAtPosition(worldPos.x, worldPos.y);
    
    // Clean up preview
    this.canvas.connectionPreview = null;
    this.canvas.isDrawingConnection = false;
    this.isDrawing = false;
    
    // Validate connection
    if (!targetNode || targetNode.id === this.sourceNode.id) {
      this.sourceNode = null;
      eventBus.emit(Events.RENDER_REQUEST);
      return null;
    }
    
    // Check for existing connection
    const existing = this.document.getAllConnections().find(
      c => (c.sourceId === this.sourceNode.id && c.targetId === targetNode.id) ||
           (c.sourceId === targetNode.id && c.targetId === this.sourceNode.id)
    );
    
    if (existing) {
      this.sourceNode = null;
      eventBus.emit(Events.RENDER_REQUEST);
      return null;
    }
    
    // Create connection
    const connection = new Connection(
      this.sourceNode.id,
      targetNode.id,
      this.connectionType
    );
    
    this.sourceNode = null;
    eventBus.emit(Events.RENDER_REQUEST);
    
    return connection;
  }

  /**
   * Cancel connection drawing
   */
  cancel() {
    this.sourceNode = null;
    this.isDrawing = false;
    this.canvas.connectionPreview = null;
    this.canvas.isDrawingConnection = false;
    eventBus.emit(Events.RENDER_REQUEST);
  }

  /**
   * Check if a position is near a node's connection handle
   * @param {number} screenX 
   * @param {number} screenY 
   * @returns {{nodeId: string, edge: string}|null}
   */
  getConnectionHandleAtPosition(screenX, screenY) {
    const threshold = 20; // pixels
    
    for (const node of this.document.getAllNodes()) {
      const bounds = node.getBounds();
      const screen = this.canvas.viewport.worldToScreen(bounds.x, bounds.y);
      const width = bounds.width * this.canvas.viewport.zoom;
      const height = bounds.height * this.canvas.viewport.zoom;
      
      // Check each edge handle
      const handles = [
        { edge: 'top', x: screen.x + width / 2, y: screen.y },
        { edge: 'right', x: screen.x + width, y: screen.y + height / 2 },
        { edge: 'bottom', x: screen.x + width / 2, y: screen.y + height },
        { edge: 'left', x: screen.x, y: screen.y + height / 2 }
      ];
      
      for (const handle of handles) {
        const dx = screenX - handle.x;
        const dy = screenY - handle.y;
        if (Math.sqrt(dx * dx + dy * dy) < threshold) {
          return { nodeId: node.id, edge: handle.edge };
        }
      }
    }
    
    return null;
  }

  /**
   * Activate/deactivate this tool
   * @param {boolean} active 
   */
  setActive(active) {
    this.isActive = active;
    if (!active) {
      this.cancel();
    }
  }
}
